import { Menu, MenuItem } from "@mui/material";
import { getLocation } from "@src/appV2/redesign/utils/routes";
import { useOpenUrl } from "@src/mobile/utils/openUrl";
import { useCallback, useState } from "react";

import { IconButton } from "../../components/IconButton";
import { Text } from "../../components/Text";

export interface ChatHeaderActionsProps {
  workerId: string;
  onViewProfile?: (workerId: string) => void;
  /**
   * Whether to show the "View Profile" icon. Defaults to true. Set to false
   * when the chat opens on a page that already shows the worker profile.
   */
  showViewProfile?: boolean;
  /**
   * When provided, renders a kebab menu with a "Mark conversation as unread"
   * action. Undefined hides the menu (e.g. admin / blocked views).
   */
  onMarkConversationAsUnread?: () => void;
}

/**
 * Action buttons for the chat header: View Profile and, when the conversation
 * can be marked unread, a kebab overflow menu.
 */
export function ChatHeaderActions(props: ChatHeaderActionsProps) {
  const { workerId, onViewProfile, showViewProfile = true, onMarkConversationAsUnread } = props;
  const openUrl = useOpenUrl();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | undefined>(undefined);

  const handleViewProfile = useCallback(() => {
    if (onViewProfile) {
      onViewProfile(workerId);
      return;
    }
    const location = getLocation("workplaceWorkerDetails", { pathParams: { workerId } });
    void openUrl(`${globalThis.location.origin}${location.pathname}`);
  }, [workerId, onViewProfile, openUrl]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(undefined);
  };

  const handleMarkAsUnread = () => {
    setMenuAnchor(undefined);
    onMarkConversationAsUnread?.();
  };

  return (
    <>
      {showViewProfile && (
        <IconButton
          aria-label="View profile"
          iconType="profile"
          size="small"
          variant="outlined"
          onClick={handleViewProfile}
        />
      )}
      {onMarkConversationAsUnread && (
        <>
          <IconButton
            aria-label="More chat options"
            iconType="ellipsis-vertical"
            size="small"
            variant="outlined"
            onClick={handleMenuOpen}
          />
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleMarkAsUnread}>
              <Text variant="body2">Mark conversation as unread</Text>
            </MenuItem>
          </Menu>
        </>
      )}
    </>
  );
}
